import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { completeVisit, fetchMyVisits } from '../api/api';
import './EngineerVisits.css';

const EngineerVisits = () => {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completionNotes, setCompletionNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Filters
  const [filter, setFilter] = useState('upcoming');

  const loadVisits = useCallback(async () => {
    setLoading(true);
    try {
      const upcoming = filter === 'upcoming';
      const days = filter === 'upcoming' ? 90 : undefined;
      console.log('[Engineer] Fetching visits - upcoming:', upcoming, 'days:', days);
      const data = await fetchMyVisits(upcoming, days);
      console.log('[Engineer] Raw visits data:', data);
      console.log('[Engineer] Visits count:', data?.length || 0);
      setVisits(data || []);
    } catch (err) {
      console.error('Failed to load visits', err);
      console.error('Error details:', err.message);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadVisits();
  }, [loadVisits]);

  const handleCompleteVisit = async () => {
    if (!selectedVisit?.id) return;

    setSubmitting(true);
    try {
      await completeVisit(selectedVisit.id, completionNotes);
      alert('Visit marked as completed');
      setShowCompleteModal(false);
      setShowDetailModal(false);
      setCompletionNotes('');
      loadVisits();
    } catch (err) {
      alert(err?.message || 'Failed to complete visit');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getDaysUntil = (dateStr) => {
    const visitDate = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffTime = visitDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return '#4CAF50';
      case 'scheduled':
        return '#F57C00';
      case 'overdue':
        return '#F44336';
      default:
        return '#666666';
    }
  };

  const getDaysColor = (days) => {
    if (days < 0) return '#F44336';
    if (days === 0) return '#FFC107';
    if (days <= 7) return '#FFC107';
    return '#4CAF50';
  };

  const getDaysText = (days) => {
    if (days < 0) return `${Math.abs(days)} days overdue`;
    if (days === 0) return 'Today';
    return `${days} days left`;
  };

  const stats = useMemo(() => ({
    scheduled: visits.filter((v) => v.status === 'scheduled').length,
    completed: visits.filter((v) => v.status === 'completed').length,
    overdue: visits.filter((v) => {
      if (v.status !== 'scheduled') return false;
      const days = getDaysUntil(v.visit_date || v.visitDate);
      return days < 0;
    }).length,
  }), [visits]);

  const filteredVisits = useMemo(() => {
    if (filter === 'all') return visits;
    return visits.filter((v) => {
      if (filter === 'completed') return v.status === 'completed';
      if (filter === 'upcoming') return v.status === 'scheduled';
      if (filter === 'overdue') {
        if (v.status !== 'scheduled') return false;
        const days = getDaysUntil(v.visit_date || v.visitDate);
        return days < 0;
      }
      return true;
    });
  }, [visits, filter]);

  return (
    <div className="engineer-visits">
      <div className="visits-header">
        <div>
          <h1>My Visits</h1>
          <p>Preventive Maintenance</p>
        </div>
        <button className="refresh-btn" onClick={loadVisits}>
          🔄
        </button>
      </div>

      <div className="filter-row">
        {['all', 'upcoming', 'overdue', 'completed'].map((f) => (
          <button
            key={f}
            className={`filter-btn ${filter === f ? 'active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Stats Summary */}
      <div className="stats-container">
        <div className="stat-item">
          <span className="stat-value">{stats.scheduled}</span>
          <span className="stat-label">Scheduled</span>
        </div>
        <div className="stat-divider"></div>
        <div className="stat-item">
          <span className="stat-value">{stats.completed}</span>
          <span className="stat-label">Completed</span>
        </div>
        <div className="stat-divider"></div>
        <div className="stat-item">
          <span className="stat-value" style={{ color: '#F44336' }}>
            {stats.overdue}
          </span>
          <span className="stat-label">Overdue</span>
        </div>
      </div>

      {loading ? (
        <div className="loading-wrap">
          <div className="spinner"></div>
          <p>Loading visits...</p>
        </div>
      ) : filteredVisits.length === 0 ? (
        <div className="empty-wrap">
          <div className="empty-icon">📅</div>
          <h3>No visits found</h3>
          <p>No visits found for filter: <strong>{filter}</strong></p>
          <button className="retry-btn" onClick={loadVisits}>
            🔄 Refresh
          </button>
        </div>
      ) : (
        <div className="visits-list">
          {filteredVisits.map((visit) => {
            const daysUntil = getDaysUntil(visit.visit_date || visit.visitDate);
            return (
              <div
                key={visit.id}
                className="visit-card"
                onClick={() => {
                  setSelectedVisit(visit);
                  setShowDetailModal(true);
                }}
              >
                <div className="visit-header">
                  <div
                    className="days-badge"
                    style={{ backgroundColor: getDaysColor(daysUntil) + '20', color: getDaysColor(daysUntil) }}
                  >
                    <span>{daysUntil < 0 ? '⚠️' : daysUntil === 0 ? '📅' : '📆'}</span>
                    <span>{getDaysText(daysUntil)}</span>
                  </div>
                  <span
                    className="status-badge"
                    style={{ backgroundColor: getStatusColor(visit.status) + '20', color: getStatusColor(visit.status) }}
                  >
                    {visit.status}
                  </span>
                </div>

                <div className="visit-info">
                  <h4>{visit.user_name || 'Unknown User'}</h4>
                  <p className="site-info">{visit.site_name || 'No site name'}</p>
                  <p className="site-address">{visit.site_address || 'No address'}</p>
                  {(visit.state || visit.area) && (
                    <div className="location-row">
                      <span>📍</span>
                      <span>
                        {[visit.area, visit.state].filter(Boolean).join(', ')}
                      </span>
                    </div>
                  )}
                </div>

                <div className="visit-footer">
                  <div className="footer-item">
                    <span>📅</span>
                    <span>{formatDate(visit.visit_date || visit.visitDate)}</span>
                  </div>
                  {visit.status === 'scheduled' && daysUntil <= 0 && (
                    <button
                      className="complete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedVisit(visit);
                        setShowCompleteModal(true);
                      }}
                    >
                      Complete
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedVisit && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Visit Details</h2>
              <button className="close-btn" onClick={() => setShowDetailModal(false)}>
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="detail-section">
                <label>Status</label>
                <span
                  className="status-badge"
                  style={{
                    backgroundColor: getStatusColor(selectedVisit.status) + '20',
                    color: getStatusColor(selectedVisit.status),
                  }}
                >
                  {selectedVisit.status}
                </span>
              </div>

              <div className="detail-section">
                <label>User Information</label>
                <p className="detail-value">{selectedVisit.user_name}</p>
                <p className="detail-subvalue">{selectedVisit.user_phone}</p>
              </div>

              <div className="detail-section">
                <label>Site Details</label>
                <p className="detail-value">{selectedVisit.site_name}</p>
                <p className="detail-subvalue">{selectedVisit.site_address}</p>
                {(selectedVisit.state || selectedVisit.area) && (
                  <p className="detail-subvalue">
                    {[selectedVisit.area, selectedVisit.state].filter(Boolean).join(', ')}
                  </p>
                )}
              </div>

              <div className="detail-section">
                <label>Visit Date</label>
                <p className="detail-value">
                  {formatDate(selectedVisit.visit_date || selectedVisit.visitDate)}
                </p>
                {selectedVisit.status === 'scheduled' && (
                  <p
                    className="detail-subvalue"
                    style={{ color: getDaysColor(getDaysUntil(selectedVisit.visit_date || selectedVisit.visitDate)) }}
                  >
                    {getDaysText(getDaysUntil(selectedVisit.visit_date || selectedVisit.visitDate))}
                  </p>
                )}
              </div>

              {selectedVisit.notes && (
                <div className="detail-section">
                  <label>Admin Notes</label>
                  <p className="detail-value">{selectedVisit.notes}</p>
                </div>
              )}

              {selectedVisit?.status === 'completed' && (
                <>
                  <div className="detail-section">
                    <label>Completed At</label>
                    <p className="detail-value">
                      {formatDate(selectedVisit.completed_at || selectedVisit.completedAt)}
                    </p>
                  </div>
                  {selectedVisit.completion_notes && (
                    <div className="detail-section">
                      <label>Completion Notes</label>
                      <p className="detail-value">{selectedVisit.completion_notes}</p>
                    </div>
                  )}
                </>
              )}

              {/* Complete Visit Button */}
              {selectedVisit.status === 'scheduled' && (
                <button
                  className="complete-visit-btn"
                  onClick={() => {
                    setShowDetailModal(false);
                    setShowCompleteModal(true);
                  }}
                >
                  <span>✓</span>
                  Mark as Completed
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Complete Visit Modal */}
      {showCompleteModal && (
        <div className="modal-overlay" onClick={() => setShowCompleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Complete Visit</h2>
              <button className="close-btn" onClick={() => setShowCompleteModal(false)}>
                ×
              </button>
            </div>

            <div className="modal-body">
              <p className="complete-desc">
                Mark this visit as completed. Add any notes about the maintenance work performed.
              </p>

              <div className="form-section">
                <label>Completion Notes</label>
                <textarea
                  className="notes-input"
                  placeholder="Describe the work done, parts replaced, observations, etc."
                  rows={4}
                  value={completionNotes}
                  onChange={(e) => setCompletionNotes(e.target.value)}
                />
              </div>

              <button
                className={`submit-btn ${submitting ? 'disabled' : ''}`}
                onClick={handleCompleteVisit}
                disabled={submitting}
              >
                {submitting ? (
                  <span className="spinner-small"></span>
                ) : (
                  <>
                    <span>✓</span>
                    <span>Complete Visit</span>
                  </>
                )}
              </button>

              <button
                className="cancel-btn"
                onClick={() => {
                  setShowCompleteModal(false);
                  setCompletionNotes('');
                }}
                disabled={submitting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EngineerVisits;
